<template>
  <div class="place-page">
    <div class="container" v-loading="loading">
      <h2 class="title">订购确认</h2>

      <template v-if="goods">
        <section class="goods-card">
          <div class="goods-head">
            <div class="goods-name">{{ goods.goodsName || "—" }}</div>
            <div class="goods-meta">
              <span v-if="goods.goodsCode" class="meta-tag">编号 {{ goods.goodsCode }}</span>
              <span v-if="billingLabel" class="meta-tag">{{ billingLabel }}</span>
              <span v-if="regionLabel" class="meta-tag">区域 {{ regionLabel }}</span>
            </div>
          </div>
          <div v-if="goods.goodsDescription" class="goods-desc">{{ goods.goodsDescription }}</div>
        </section>

        <el-form ref="form" :model="form" :rules="rules" label-width="100px" class="form">
          <el-form-item label="数量" prop="quantity">
            <el-input-number v-model="form.quantity" :min="1" :max="999" />
          </el-form-item>
          <el-form-item label="时长" prop="duration">
            <el-input-number v-model="form.duration" :min="1" :max="120" />
            <el-select v-model="form.durationUnit" class="unit-select">
              <el-option label="月" value="MONTH" />
              <el-option label="年" value="YEAR" />
            </el-select>
          </el-form-item>
          <el-form-item label="备注">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="2"
              placeholder="选填，例如对接联系人或特殊要求"
              maxlength="200"
              show-word-limit
            />
          </el-form-item>
        </el-form>

        <div class="hint">
          <i class="el-icon-info"></i>
          提交后将跳转至支付页完成付款，最终金额以支付页为准。
        </div>

        <div class="actions">
          <el-button @click="onCancel">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="onSubmit">提交订单</el-button>
        </div>
      </template>

      <el-empty v-else-if="!loading" description="未找到对应商品" />
    </div>
  </div>
</template>

<script>
import { goodsDetail, placeOrder } from "@/api/index";
import { authGuardRedirect } from "@/utils/ncnp";
import { getToken } from "@/utils/auth";

const BILLING_LABELS = {
  HOUR: "按小时计费",
  MONTH: "按月计费",
  YEAR: "按年计费",
  PACKAGE: "包年包月"
};

// 区域数字 → 中文（与后端 GoodsSyncServiceImpl.mapRegion 映射一致：1上海/2江苏/3浙江/4安徽）
const REGION_LABELS = {
  1: "上海",
  2: "江苏",
  3: "浙江",
  4: "安徽"
};

const SAFE_PATHS = ["/compute", "/infra", "/orders", "/user", "/ncnp"];

function isSafeRedirect(target) {
  if (typeof target !== "string" || target.length === 0) return false;
  if (/^https?:\/\//i.test(target)) return false;
  if (target.startsWith("//")) return false;
  if (!target.startsWith("/")) return false;
  return SAFE_PATHS.some(
    (p) => target === p || target.startsWith(`${p}/`) || target.startsWith(`${p}?`)
  );
}

export default {
  name: "OrdersPlace",
  data() {
    return {
      loading: false,
      submitting: false,
      goods: null,
      form: {
        quantity: 1,
        duration: 1,
        durationUnit: "MONTH",
        remark: ""
      },
      rules: {
        quantity: [{ required: true, message: "请填写数量", trigger: "change" }],
        duration: [{ required: true, message: "请填写时长", trigger: "change" }]
      }
    };
  },
  computed: {
    goodsId() {
      // 兼容 NCNP 上架用的驼峰 goodsId 与站内下划线 goods_id
      const raw = this.$route.query.goods_id || this.$route.query.goodsId;
      if (!raw) return null;
      // 雪花ID(19位)超过 JS Number 精度(2^53≈16位)，Number() 会丢末位精度，
      // 导致 goodsDetail 查不到 → GoodsWrapper.entityVO(null) NPE(500)。
      // 后端 jackson big-num-to-string 已把 Long 序列化为字符串，
      // 此处保留字符串原值，后端 GoodsEntity.id(Long) 由 Spring 直接收字符串绑定，不丢精度。
      return raw;
    },
    billingLabel() {
      if (!this.goods) return "";
      const key = (this.goods.billingMethod || "").toUpperCase();
      return BILLING_LABELS[key] || this.goods.billingMethod || "";
    },
    regionLabel() {
      // region 为数字枚举；映射不中则回退原值，避免出现"区域 1"这类裸数字
      if (!this.goods || this.goods.region == null) return "";
      return REGION_LABELS[this.goods.region] || this.goods.region;
    }
  },
  created() {
    if (!this.goodsId) {
      this.$message.error("缺少商品参数");
      this.$router.replace("/compute");
      return;
    }
    this.loadGoods();
  },
  methods: {
    async loadGoods() {
      this.loading = true;
      try {
        const res = await goodsDetail({ id: this.goodsId });
        const data = res && res.data && res.data.data ? res.data.data : (res && res.data ? res.data : null);
        if (!data || !data.id) {
          this.goods = null;
          return;
        }
        this.goods = data;
      } catch (e) {
        this.$message.error("加载商品失败");
      } finally {
        this.loading = false;
      }
    },

    onCancel() {
      this.$router.back();
    },

    async onSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (e) {
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          goodsId: this.goods.id,
          extGoodsId: this.goods.ncnpResourceId || undefined,
          quantity: this.form.quantity,
          duration: this.form.duration,
          durationUnit: this.form.durationUnit,
          billingMode: (this.goods.billingMethod || "").toUpperCase() || undefined,
          remark: this.form.remark || undefined
        };
        const res = await placeOrder(payload);
        const body = res && res.data ? res.data : {};
        // BladeX 标准 R<T>：{ code, data, msg, success }
        if (body.success === false || (body.code != null && body.code !== 200)) {
          this.$message.error(body.msg || "提交订单失败");
          return;
        }
        const data = body.data || body;
        this.dispatchByPayType(data);
      } catch (e) {
        // 401/403 让全局拦截器处理；这里尽量透传后端业务错误 msg
        // （如金额挡单"商品规格未同步，请先从 PaaS 同步商品"），拿不到再兜底。
        const data = e && e.response && e.response.data;
        const msg = (data && (data.msg || data.message)) || "提交订单失败";
        this.$message.error(msg);
      } finally {
        this.submitting = false;
      }
    },

    /**
     * 按 payType 分流（契约见 OrderPlaceResponse）：
     *  - NCNP：跳 NCNP welcome 支付页（ncnpPayUrl）
     *  - EXTERNAL：跳外部 pay_url
     *  - 兜底：跳本站 /orders/pay 携 order_id
     */
    dispatchByPayType(resp) {
      if (!resp || !resp.orderId) {
        this.$message.error("订单创建异常");
        return;
      }
      const payType = (resp.payType || "").toUpperCase();
      if (payType === "NCNP" && resp.ncnpPayUrl) {
        window.location.href = resp.ncnpPayUrl;
        return;
      }
      if (payType === "EXTERNAL" && resp.externalPayUrl) {
        window.location.href = resp.externalPayUrl;
        return;
      }
      const fallback = `/orders/pay?order_id=${encodeURIComponent(resp.orderId)}`;
      this.$router.push(isSafeRedirect(fallback) ? fallback : "/infra");
    }
  },

  beforeRouteEnter(to, from, next) {
    // 未登录用户在路由守卫层应已被拦截；此处做一次防御兜底，与 permission.js 一致以
    // localStorage.userToken（getToken）为判据。
    // 注意：user 模块未暴露 token getter，不能用 getters["user/token"]——
    // 该值恒为 undefined，会导致登录态被误判为未登录，进而 authGuardRedirect 反复触发
    // NCNP 授权码流程，形成 /ncnp/callback ↔ /orders/place 的无限重定向循环。
    next((vm) => {
      if (!getToken()) {
        authGuardRedirect(to);
      }
    });
  }
};
</script>

<style scoped lang="scss">
.place-page {
  min-height: 60vh;
  background: #f6f8fb;
  padding: 32px 0;
}

.container {
  max-width: 880px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(22, 119, 255, 0.06);
  padding: 32px;
}

.title {
  margin: 0 0 24px;
  font-size: 22px;
  color: #1d2129;
}

.goods-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 24px;

  .goods-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  .goods-name {
    font-size: 18px;
    color: #1d2129;
    font-weight: 600;
  }

  .goods-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .meta-tag {
    font-size: 12px;
    color: #4e5969;
    background: #f2f3f5;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .goods-desc {
    margin-top: 12px;
    color: #4e5969;
    line-height: 1.6;
    white-space: pre-wrap;
  }
}

.form {
  .unit-select {
    width: 100px;
    margin-left: 12px;
  }
}

.hint {
  margin: 12px 0 20px;
  padding: 10px 14px;
  background: #ecf5ff;
  color: #1677ff;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.6;

  .el-icon-info {
    margin-right: 4px;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}
</style>
